import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import { EditProgram, Program } from "../../types";
import PageHeader from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import { Link } from "react-router-dom";
import ProgramModal from "../../components/common/Program/ProgramModal/ProgramModal";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import { useSnackbar } from "notistack";
import { observer } from "mobx-react";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import EmptySpace from "../../components/common/EmptySpace";
import Loading from "../../components/common/Loading";
import moment from "moment";
import { classic } from "../../config/colors";
import { useHandleMediaFolderChange } from "../../hooks/vimeoFoldersAndVideos/useHandleMediaFolderChange";

const ProgramsList: React.FC = observer(() => {
  const { programStore, categoryStore, bannerStore, adCircuitStore } =
    useStores();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const modalHandler = () => {
    setIsModalOpen((open) => !open);
  };
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setIsLoading(true);
    categoryStore.getProgramCategories();

    bannerStore.getBanners();
    adCircuitStore.getAdCircuits();

    programStore
      .getProgram()
      .then(() => setIsLoading(false))
      .catch((err) => {
        enqueueSnackbar(err.message, {
          variant: "error",
        });
      });
  }, []);

  const handleSubmit = useCallback(
    async (data: EditProgram) => {
      setIsLoading(true);
      data.categories = data.categories.map((x: any) => {
        return parseInt(x, 10);
      });
      try {
        await programStore.addProgram(data).then(() => {
          setIsLoading(false);
        });
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }

      modalHandler();
    },
    [enqueueSnackbar, programStore, t]
  );

  const handleUpdate = useCallback(
    async (data: Program) => {
      setIsLoading(true);

      try {
        await programStore
          .updateProgram({
            ...data,
            categories: data.categories
              ? data.categories.map((x: any) => {
                  return parseInt(x, 10);
                })
              : [],
          } as EditProgram)
          .then(() => {
            setIsLoading(false);
          });
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, programStore, t]
  );

  const handleDeleteProgram = useCallback(
    async (id: string) => {
      setIsLoading(true);

      try {
        await programStore.deleteProgram(id).then(() => {
          setIsLoading(false);
        });
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, programStore, t]
  );

  const handleMediaFolderChange = useHandleMediaFolderChange({ programStore });

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 2,
        label: t("screens.program.column.title"),
        dataKey: "title",
        sorter: (a: { name: string }, b: { name: any }) =>
          a.name.localeCompare(b.name),
        render: (name: string, program: Program) => {
          return program.deleted ? (
            <span style={{ color: classic.shuttle }}>{name}</span>
          ) : (
            <Link to={"/program/" + program.id}>{name}</Link>
          );
        },
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.program.column.validFrom"),
        dataKey: "validFrom",
        render: (validFrom: Date, program: Program) => {
          const formattedDate = moment(validFrom).format("DD/MM/YYYY");
          return program.deleted ? (
            <span style={{ color: classic.shuttle }}>{formattedDate}</span>
          ) : (
            formattedDate
          );
        },
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.program.column.validUntil"),
        dataKey: "validUntil",
        render: (validUntil: Date, program: Program) => {
          const formattedDate = moment(validUntil).format("DD/MM/YYYY");
          return program.deleted ? (
            <span style={{ color: classic.shuttle }}>{formattedDate}</span>
          ) : (
            formattedDate
          );
        },
      },
      {
        width: 90,
        flexGrow: 0,
        label: t("common.action"),
        dataKey: "id",
        render: (id: string, program: Program) => {
          return program.deleted ? (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
              }}
            >
              <PopupState variant="popover" popupId="demo-popup-popover-1">
                {(popupState) => (
                  <div>
                    <IconButton color="primary" {...bindTrigger(popupState)}>
                      <RestoreIcon />
                    </IconButton>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <Typography sx={{ p: 1 }}>
                        {t("screens.program.column.restore")}
                        <IconButton
                          color="primary"
                          sx={{ p: 0 }}
                          onClick={() =>
                            handleUpdate({
                              ...program,
                              deleted: false,
                            })
                          }
                        >
                          <RestoreIcon />
                        </IconButton>
                      </Typography>
                    </Popover>
                  </div>
                )}
              </PopupState>
              <PopupState variant="popover" popupId="demo-popup-popover-2">
                {(popupState) => (
                  <div>
                    <IconButton color="error" {...bindTrigger(popupState)}>
                      <DeleteIcon />
                    </IconButton>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <Typography sx={{ p: 1 }}>
                        {t("common.deletePopUp")}
                        <IconButton
                          color="error"
                          sx={{ p: 0 }}
                          onClick={() => handleDeleteProgram(id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Typography>
                    </Popover>
                  </div>
                )}
              </PopupState>
            </div>
          ) : (
            <PopupState variant="popover" popupId="demo-popup-popover">
              {(popupState) => (
                <div>
                  <IconButton color="primary" {...bindTrigger(popupState)}>
                    <DeleteIcon />
                  </IconButton>
                  <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                  >
                    <Typography sx={{ p: 1 }}>
                      {t("screens.program.column.firstDeletePopUp")}
                      <IconButton
                        color="primary"
                        sx={{ p: 0 }}
                        onClick={() =>
                          handleUpdate({
                            ...program,
                            deleted: true,
                          })
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Typography>
                  </Popover>
                </div>
              )}
            </PopupState>
          );
        },
      },
    ],
    [handleDeleteProgram, handleUpdate, t]
  );

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px" align="top">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.program.breadcrumb.home")}
          </BreadcrumbItem>
          <BreadcrumbItem active>
            {t("screens.program.breadcrumb.programs")}
          </BreadcrumbItem>
        </Breadcrumb>

        <PageHeader
          title={t("screens.program.pageHeader.title")}
          buttonEnable={true}
          buttonText={t("screens.program.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      </Content>
      {isLoading ? (
        <Loading />
      ) : !isLoading && programStore.programs.length > 0 ? (
        <Content fullHeight align="top" mt={[null, null, null, "0px"]}>
          <div
            style={{
              flex: "1 1 auto",
              width: "100%",
              height: `${(programStore.programs.length + 1) * 50}px`,
              display: "flex",
              marginTop: "20px",
            }}
          >
            <VirtualizedTable
              rowCount={programStore.programs.length}
              rowGetter={({ index }) => programStore.programs[index]}
              columns={columns}
              rowStyle={(row) =>
                row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
              }
            />
          </div>
        </Content>
      ) : (
        <EmptySpace
          title={t("screens.program.emptyData")}
          buttonEnable={true}
          buttonText={t("screens.program.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      )}

      <ProgramModal
        program={undefined}
        isSubmitting={isLoading}
        isOpen={isModalOpen}
        onClose={modalHandler}
        onSubmit={handleSubmit}
        onChangeFolder={handleMediaFolderChange}
        medias={programStore.mediaList}
      />
    </Layout>
  );
});

export default ProgramsList;
